/**
 * Persist demand editable fields in localStorage so Save / Record Payment
 * survive refresh when the API is offline, returns stale rows, or drops columns.
 */

import { evidenceFileKey, fileNamesFromEvidenceUrl } from './evidenceFiles.js';
import {
  listMatterPaymentEntries,
  matterAmountPaid,
} from './paidBeforeAssignment.js';

const KEY = 'demandjet.matterPatches';

const PATCH_KEYS = [
  'Debtor_Name',
  'Debtor_Company',
  'Debtor_Country',
  'Debtor_Street',
  'Debtor_City',
  'Debtor_State',
  'Debtor_Postal_Code',
  'Debtor_Email',
  'Amount_Owed',
  'Amount_Paid',
  'Payment_Entries',
  'Paid_At',
  'Paid_Source',
  'Status',
  'Status_Before_Paid',
  'External_Invoice_ID',
  'External_Account_Number',
  'Dispute_Type',
  'Dispute_Other',
  'Evidence_Vault_URL',
  'Document_Vault_URL',
  'evidenceFileNames',
  'Installment_Plan',
  'Payment_Structure',
  'Payment_Term_ID',
  'Settlement_Choice',
  'Attachments',
  'Demand_Fee_Charged',
  'Demand_Fee_Amount',
  'Demand_Fee_Waived',
  'Demand_Fee_Waived_Reason',
  'Resolution_Note',
  'Updated_At',
];

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map) {
  try {
    localStorage.setItem(KEY, JSON.stringify(map || {}));
  } catch {
    // Quota / private mode — data still lives in React state for this session.
  }
}

function pickPatchFields(matter) {
  if (!matter || typeof matter !== 'object') return {};
  const out = {};
  for (const key of PATCH_KEYS) {
    if (matter[key] !== undefined) out[key] = matter[key];
  }
  return out;
}

function evidenceNameCount(urlOrNames) {
  if (Array.isArray(urlOrNames)) {
    return urlOrNames.map((n) => String(n || '').trim()).filter(Boolean).length;
  }
  return fileNamesFromEvidenceUrl(urlOrNames).length;
}

function paymentEntryCount(matter) {
  return listMatterPaymentEntries(matter).length;
}

/** True when the patch has payment data the server/seed row is missing. */
function patchHasMorePayments(patch, matter) {
  const patchEntries = paymentEntryCount(patch);
  const matterEntries = paymentEntryCount(matter);
  if (patchEntries > matterEntries) return true;
  const patchPaid = matterAmountPaid(patch);
  const matterPaid = matterAmountPaid(matter);
  return patchPaid > matterPaid + 0.001;
}

/**
 * Prefer the local payment ledger when it is newer (including deletes that
 * reduce entry count), or when it fills payments the server/seed is missing.
 */
function patchPaymentLedgerWins(patch, matter) {
  if (patch.Payment_Entries === undefined && patch.Amount_Paid === undefined) {
    return false;
  }
  const patchTime = Date.parse(patch.Updated_At || '') || 0;
  const matterTime = Date.parse(matter.Updated_At || '') || 0;
  if (patchTime >= matterTime) return true;
  return patchHasMorePayments(patch, matter);
}

export function getMatterPatch(matterId) {
  const id = String(matterId || '');
  if (!id) return null;
  const entry = readAll()[id];
  return entry && typeof entry === 'object' ? entry : null;
}

/** Store editable fields for a matter (merges with any existing patch). */
export function saveMatterPatch(matterId, matter) {
  const id = String(matterId || '');
  if (!id || !matter) return null;
  const all = readAll();
  const prev = all[id] && typeof all[id] === 'object' ? all[id] : {};
  const picked = pickPatchFields(matter);
  // Always keep an explicit file-name list when we have a vault URL.
  if (
    picked.evidenceFileNames === undefined &&
    picked.Evidence_Vault_URL !== undefined
  ) {
    picked.evidenceFileNames = fileNamesFromEvidenceUrl(picked.Evidence_Vault_URL);
  }
  // Normalize payment entries to a plain array for stable JSON storage.
  if (picked.Payment_Entries !== undefined) {
    picked.Payment_Entries = listMatterPaymentEntries({
      Payment_Entries: picked.Payment_Entries,
    });
  }
  const next = {
    ...prev,
    ...picked,
    Updated_At: matter.Updated_At || new Date().toISOString(),
  };
  all[id] = next;
  writeAll(all);
  return next;
}

export function clearMatterPatch(matterId) {
  const id = String(matterId || '');
  if (!id) return;
  const all = readAll();
  if (!(id in all)) return;
  delete all[id];
  writeAll(all);
}

/**
 * Clear the local patch only when the server row already has the same evidence files.
 * Keeps the patch when the API "succeeds" but returns a stale/missing vault URL.
 */
export function clearMatterPatchIfEvidenceSynced(matterId, serverMatter, savedEvidenceUrl) {
  const serverKey = evidenceFileKey(serverMatter?.Evidence_Vault_URL);
  const savedKey = evidenceFileKey(savedEvidenceUrl);
  if (savedKey && serverKey === savedKey) {
    clearMatterPatch(matterId);
    return true;
  }
  return false;
}

/**
 * Overlay a local patch onto a loaded matter.
 * Evidence / payment data from the patch wins when the server/seed row is missing it.
 */
export function applyMatterPatchToRow(matter) {
  if (!matter?.ROWID) return matter;
  const patch = getMatterPatch(matter.ROWID);
  if (!patch) return matter;

  const patchTime = Date.parse(patch.Updated_At || '') || 0;
  const matterTime = Date.parse(matter.Updated_At || '') || 0;
  const patchEvidence = patch.Evidence_Vault_URL;
  const matterEvidence = matter.Evidence_Vault_URL;
  const patchKey = evidenceFileKey(patchEvidence);
  const matterKey = evidenceFileKey(matterEvidence);
  const patchHasMoreEvidence =
    Boolean(patchKey) &&
    patchKey !== matterKey &&
    evidenceNameCount(patch.evidenceFileNames || patchEvidence) >=
      evidenceNameCount(matterEvidence);
  const paymentLedgerWins = patchPaymentLedgerWins(patch, matter);

  // Newer patch, or evidence/payments the server/seed is missing — apply.
  // Newer patches also win for locker/demand deletes (null / fewer files).
  if (patchTime >= matterTime || patchHasMoreEvidence || paymentLedgerWins) {
    const merged = { ...matter, ...pickPatchFields(patch), ROWID: matter.ROWID };
    if (Array.isArray(patch.evidenceFileNames)) {
      merged.evidenceFileNames = patch.evidenceFileNames;
    }
    if (paymentLedgerWins) {
      merged.Payment_Entries = listMatterPaymentEntries(patch);
      if (patch.Amount_Paid !== undefined) merged.Amount_Paid = patch.Amount_Paid;
      if (patch.Status) merged.Status = patch.Status;
      // Allow null Paid_At when a delete reopens a previously paid demand.
      if ('Paid_At' in patch) merged.Paid_At = patch.Paid_At;
      if ('Paid_Source' in patch) merged.Paid_Source = patch.Paid_Source;
      if ('Status_Before_Paid' in patch) {
        merged.Status_Before_Paid = patch.Status_Before_Paid;
      }
      if ('Demand_Fee_Waived' in patch) merged.Demand_Fee_Waived = patch.Demand_Fee_Waived;
      if ('Demand_Fee_Waived_Reason' in patch) {
        merged.Demand_Fee_Waived_Reason = patch.Demand_Fee_Waived_Reason;
      }
      if ('Demand_Fee_Charged' in patch) merged.Demand_Fee_Charged = patch.Demand_Fee_Charged;
      if ('Demand_Fee_Amount' in patch) merged.Demand_Fee_Amount = patch.Demand_Fee_Amount;
      if ('Resolution_Note' in patch) merged.Resolution_Note = patch.Resolution_Note;
    }
    return merged;
  }

  return matter;
}

/** Overlay local patches onto matter rows (by ROWID). */
export function applyMatterPatches(matters = []) {
  const all = readAll();
  if (!Object.keys(all).length) return matters || [];
  return (matters || []).map((matter) => applyMatterPatchToRow(matter));
}
